import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import {
  DataTablePrimaryText,
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";

export type AssignmentKind = "activity" | "house";

export type PendingRemove = { kind: AssignmentKind; id: string };

export function buildAssignmentColumns<TRow, TSort extends string>({
  entityHeader,
  ownerHeader,
  rateHeader,
  removeLabel,
  entitySortKey,
  ownerSortKey,
  getEntityId,
  getEntityName,
  getOwnerId,
  getOwnerName,
  getRate,
  removeKind,
  pendingRemove,
  isRemovePending,
  onRequestRemove,
}: {
  entityHeader: string;
  ownerHeader: string;
  rateHeader: string;
  removeLabel: string;
  entitySortKey: TSort;
  ownerSortKey: TSort;
  getEntityId: (row: TRow) => string;
  getEntityName: (row: TRow) => string;
  getOwnerId: (row: TRow) => string;
  getOwnerName: (row: TRow) => string;
  getRate: (row: TRow) => number;
  removeKind: AssignmentKind;
  pendingRemove: PendingRemove | null;
  isRemovePending: boolean;
  onRequestRemove: (id: string) => void;
}): DataTableColumn<TRow, TSort>[] {
  return [
    {
      // Keep stable ids aligned with sort keys so DataTable preferences
      // (widths / visibility) stay compatible with the pre-refactor tables.
      id: entitySortKey,
      header: entityHeader,
      sortKey: entitySortKey,
      sortKind: "text",
      getSortValue: (row) => getEntityName(row),
      defaultWidth: 220,
      cell: (row) => (
        <div className="min-w-0">
          <DataTablePrimaryText>{getEntityName(row)}</DataTablePrimaryText>
          <div className="text-xs text-surface-500">
            <ClickToCopyId
              value={getEntityId(row)}
              className="text-surface-500"
            />
          </div>
        </div>
      ),
    },
    {
      id: ownerSortKey,
      header: ownerHeader,
      sortKey: ownerSortKey,
      sortKind: "text",
      getSortValue: (row) => getOwnerName(row),
      defaultWidth: 180,
      cell: (row) => (
        <div className="min-w-0">
          <DataTablePrimaryText>{getOwnerName(row)}</DataTablePrimaryText>
          <div className="text-xs text-surface-500">
            <ClickToCopyId
              value={getOwnerId(row)}
              className="text-surface-500"
            />
          </div>
        </div>
      ),
    },
    {
      id: "rate",
      header: rateHeader,
      sortKey: "rate" as TSort,
      sortKind: "number",
      getSortValue: (row) => getRate(row),
      defaultWidth: 120,
      cell: (row) => (
        <span className="tabular-nums text-surface-900">
          {formatCommissionPercentLabel(getRate(row))}
        </span>
      ),
    },
    createActionsColumn("", {
      defaultWidth: 120,
      cell: (row) => {
        const entityId = getEntityId(row);
        const isRemoving =
          isRemovePending &&
          pendingRemove?.kind === removeKind &&
          pendingRemove.id === entityId;

        return (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-error-700 hover:border-error-300 hover:bg-error-50 hover:text-error-700 dark:hover:bg-error-50"
            disabled={isRemovePending}
            isLoading={isRemoving}
            leftIcon={!isRemoving ? <Trash2 className="h-4 w-4" /> : undefined}
            onClick={() => onRequestRemove(entityId)}
          >
            {removeLabel}
          </Button>
        );
      },
    }),
  ];
}
